import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/** Line property on free BOGO tubs (value = discount code). */
const BOGO_GIFT_PROPERTY = '_bogo_gift';
/** Cart attributes: offer switched on (value = code), the tub variant, and the drawer notice ("removed:CODE" | "rejected"). */
const BOGO_ACTIVE_ATTR = '_bogo_active';
const BOGO_VARIANT_ATTR = '_bogo_variant';
const BOGO_NOTICE_ATTR = '_bogo_notice';
/** localStorage hint written on claim: { code, variant, token }. Only trusted for the same cart token. */
const BOGO_HINT_KEY = 'next50_bogo_active';

/** @param {any} item */
const bogoGiftCode = (item) => item?.properties?.[BOGO_GIFT_PROPERTY] || '';

/** @param {unknown} a @param {unknown} b */
const sameCode = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();

/** @param {any} resource */
const isCartJson = (resource) => Array.isArray(resource?.items) && typeof resource?.item_count === 'number';

/** @returns {{ code?: string, variant?: string | number, token?: string } | null} */
function readBogoHint() {
  try {
    return JSON.parse(localStorage.getItem(BOGO_HINT_KEY) || 'null');
  } catch {
    return null;
  }
}

/** @param {{ code: string, variant: string | number, token?: string } | null} value */
function writeBogoHint(value) {
  try {
    if (value) localStorage.setItem(BOGO_HINT_KEY, JSON.stringify(value));
    else localStorage.removeItem(BOGO_HINT_KEY);
  } catch {
    /* storage blocked */
  }
}

/** Shared by the drawer and cart-page instances so only one gift sync runs at a time. */
const bogoSync = { inFlight: false, pending: false };

/**
 * BOGO state of a cart. The offer is active when the cart carries the _bogo_active attribute, or the code is
 * applied and a gift line exists, or the claim hint belongs to this cart. While active, the gift line quantity
 * must equal the paid tubs of the gift variant (subscription + one-off), capped at maxGifts (0 = no cap).
 * @param {any} cart
 * @param {number} maxGifts
 */
function bogoState(cart, maxGifts) {
  const items = Array.isArray(cart?.items) ? cart.items : [];
  const attributes = cart?.attributes || {};
  const codes = Array.isArray(cart?.discount_codes) ? cart.discount_codes : [];
  const gifts = items.filter(bogoGiftCode);
  const giftKeys = gifts.map((/** @type {any} */ item) => String(item.key));
  const giftTotal = gifts.reduce((/** @type {number} */ sum, /** @type {any} */ item) => sum + item.quantity, 0);
  const codeEntry = (/** @type {string} */ code) => codes.find((/** @type {any} */ entry) => sameCode(entry.code, code));
  const hint = readBogoHint();

  let code = String(attributes[BOGO_ACTIVE_ATTR] || '');
  if (!code && gifts.length && codeEntry(bogoGiftCode(gifts[0]))) code = bogoGiftCode(gifts[0]);
  if (!code && hint?.code && hint.token && cart?.token && hint.token === cart.token) code = hint.code;

  const variantId = Number(attributes[BOGO_VARIANT_ATTR] || gifts[0]?.variant_id || hint?.variant || 0);
  const paidTubs = items
    .filter((/** @type {any} */ item) => !bogoGiftCode(item) && Number(item.variant_id) === variantId)
    .reduce((/** @type {number} */ sum, /** @type {any} */ item) => sum + item.quantity, 0);
  const entry = code ? codeEntry(code) : null;

  return {
    active: Boolean(code),
    code,
    variantId,
    giftKeys,
    giftTotal,
    paidTubs,
    target: code ? (maxGifts > 0 ? Math.min(paidTubs, maxGifts) : paidTubs) : 0,
    codeApplied: Boolean(entry),
    codeRejected: entry?.applicable === false,
    attributesCurrent:
      sameCode(attributes[BOGO_ACTIVE_ATTR], code) &&
      String(attributes[BOGO_VARIANT_ATTR] || '') === String(variantId || '') &&
      !attributes[BOGO_NOTICE_ATTR],
  };
}

/**
 * /cart/update.js quantities putting `quantity` on the first key and zeroing the rest.
 * @param {string[]} keys
 * @param {number} quantity
 */
function bogoKeyUpdates(keys, quantity) {
  /** @type {Record<string, number>} */
  const updates = {};
  keys.forEach((key, index) => {
    updates[key] = index === 0 ? quantity : 0;
  });
  return updates;
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  /** @type {boolean} */
  #sellingPlanUpdateInFlight = false;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.addEventListener('change', this.#onSellingPlanChange);
    this.addEventListener('click', this.#onSellingPlanClick);
    this.addEventListener('click', this.#onCheckoutClick);
    this.addEventListener('click', this.#onBogoReactivateClick);
    window.addEventListener('pageshow', this.#onPageShow);

    // The cart may have changed since the last visit (another tab, the /cart page, an earlier session).
    if (this.#bogoWatching()) {
      setTimeout(() => this.#syncBogoGifts(), 0);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.removeEventListener('change', this.#onSellingPlanChange);
    this.removeEventListener('click', this.#onSellingPlanClick);
    this.removeEventListener('click', this.#onCheckoutClick);
    this.removeEventListener('click', this.#onBogoReactivateClick);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted && this.#bogoWatching()) this.#syncBogoGifts();
  };

  /**
   * Whether this cart may need BOGO gift sync: gift rows, the active marker rendered from the cart attribute,
   * or a claim hint in this browser. Keeps /cart.js fetches off carts that never claimed the offer.
   * @returns {boolean}
   */
  #bogoWatching() {
    return Boolean(this.querySelector('[data-bogo-gift], [data-bogo-active]') || readBogoHint());
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity: displayQty, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    // Double-subscription lines step as a bundle (1 bundle = N jars). The stepper displays
    // bundles, but the cart line quantity must be expressed in jars.
    const jarsPerBundle = this.#getJarsPerBundle(event.target, line);
    const quantity = displayQty * jarsPerBundle;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    const groupKeys = this.#getGroupKeys(line);
    this.updateLineItem({
      line,
      quantity,
      action: 'change',
      ...(groupKeys.length > 1 ? { updates: this.#groupUpdates(groupKeys, quantity) } : {}),
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Reads the jars-per-bundle multiplier for a cart line's quantity selector.
   * Falls back to the line's quantity selector ref when the event target is unavailable.
   * @param {EventTarget | null} target
   * @param {number} line - 1-based cart line index.
   * @returns {number}
   */
  #getJarsPerBundle(target, line) {
    let input =
      target instanceof Element ? target.querySelector('input[data-jars-per-bundle]') : null;

    if (!input) {
      const quantitySelector = this.refs.quantitySelectors?.[line - 1];
      input = quantitySelector?.querySelector('input[data-jars-per-bundle]') ?? null;
    }

    const parsed = input ? Number.parseInt(input.getAttribute('data-jars-per-bundle') ?? '1', 10) : 1;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  /**
   * Ensures card clicks select the radio and fire change (visually-hidden radios).
   * @param {Event} event
   */
  #onSellingPlanClick = (event) => {
    const card = event.target instanceof Element ? event.target.closest('.next50-selling-plan__card') : null;
    if (!card || !this.contains(card)) return;

    const option = card.closest('.next50-selling-plan__option');
    const radio = option?.querySelector('[data-selling-plan-radio]');
    if (!(radio instanceof HTMLInputElement)) return;

    if (!radio.checked) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /**
   * Handles selling plan radio change in the cart.
   * @param {Event} event
   */
  #onSellingPlanChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio' || !target.hasAttribute('data-selling-plan-radio')) {
      return;
    }
    if (!this.contains(target)) return;
    if (this.#sellingPlanUpdateInFlight) return;

    const fieldset = target.closest('[data-cart-line]');
    const line = fieldset ? Number.parseInt(fieldset.dataset.cartLine ?? '', 10) : NaN;
    // Cart lines are 1-based; guard against NaN / invalid indices only.
    if (Number.isNaN(line) || line < 1) return;

    const cartQtyAttr = target.getAttribute('data-cart-qty');
    let quantity = cartQtyAttr ? Number.parseInt(cartQtyAttr, 10) : NaN;
    if (Number.isNaN(quantity) || quantity < 1) {
      const quantitySelector = this.refs.quantitySelectors?.[line - 1];
      const quantityInput = quantitySelector?.querySelector('input[name="updates[]"]');
      const displayQty = quantityInput ? Number.parseInt(quantityInput.value, 10) : 1;
      const jarsPerBundle = this.#getJarsPerBundle(quantityInput ?? quantitySelector, line);
      quantity = (Number.isNaN(displayQty) || displayQty < 1 ? 1 : displayQty) * jarsPerBundle;
    }

    /** @type {number | ''} */
    const sellingPlan = target.value === '' ? '' : Number.parseInt(target.value, 10);
    if (target.value !== '' && Number.isNaN(sellingPlan)) return;

    const purchaseOption = target.getAttribute('data-purchase-option') || (sellingPlan === '' ? 'buy_once' : null);

    this.updateLineItem({
      line,
      quantity,
      selling_plan: sellingPlan,
      purchase_option: purchaseOption,
      action: 'change-selling-plan',
    });

    const lineItemRow = this.refs.cartItemRows[line - 1];
    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow?.querySelector('text-component'));
    textComponent?.shimmer();
  };

  /**
   * Checkout must not POST cart-form updates[] — quantity inputs show bundle counts
   * while the server cart stores jars. Redirect to /checkout so AJAX cart state is used.
   * @param {Event} event
   */
  #onCheckoutClick = (event) => {
    const checkoutControl =
      event.target instanceof Element
        ? event.target.closest('[data-cart-checkout], [name="checkout"][form="cart-form"]')
        : null;
    if (!checkoutControl || !this.contains(checkoutControl)) return;

    if (this.#sellingPlanUpdateInFlight) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    window.location.assign('/checkout');
  };

  /**
   * Reads the Shopify line-item key for a 1-based cart line.
   * @param {number} line
   * @returns {string | null}
   */
  #getLineItemKey(line) {
    const lineItemRow = this.refs.cartItemRows?.[line - 1];
    const key = lineItemRow?.dataset.key;
    return key || null;
  }

  onLineItemRemove(line) {
    const giftRow = this.refs.cartItemRows?.[line - 1];
    if (giftRow?.dataset.bogoGift) {
      // Removing the FREE row turns the offer off (otherwise the sync would add it straight back).
      this.#setBogoOffer(false, giftRow.dataset.bogoGift);
      return;
    }

    const groupKeys = this.#getGroupKeys(line);
    this.updateLineItem({
      line,
      quantity: 0,
      action: 'clear',
      ...(groupKeys.length > 1 ? { updates: this.#groupUpdates(groupKeys, 0) } : {}),
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
      // Hidden lines folded into this row (Buy X Get Y splits / property mismatches)
      ...this.refs.cartItemRows.filter(
        (row) => row !== cartItemRowToRemove && row.dataset.key && groupKeys.includes(row.dataset.key)
      ),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates a cart line (quantity and/or selling plan).
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {number | ''} [config.selling_plan] - Selling plan id, or empty string for one-time.
   * @param {string | null} [config.purchase_option] - Tracking label: single_subscription | double_subscription | buy_once
   * @param {string} config.action - The action.
   */
  updateLineItem(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const isSellingPlanChange = config.action === 'change-selling-plan';

    if (isSellingPlanChange) {
      this.#sellingPlanUpdateInFlight = true;
    }

    this.#disableCartItems();

    const { line, quantity, selling_plan: sellingPlan, purchase_option: purchaseOption } = config;
    const { cartTotal } = this.refs;

    const sectionsToUpdate = this.#sectionsToUpdate();

    const lineKey = this.#getLineItemKey(line);
    const sourceRow = this.refs.cartItemRows?.[line - 1];
    const sourceKeys = this.#getGroupKeys(line);
    const sourcePlanId = sourceRow?.dataset.sellingPlanId ?? '';
    const variantId = sourceRow?.dataset.variantId ?? '';
    const mergeTarget = isSellingPlanChange && lineKey ? this.#findMergeTarget(line, sellingPlan) : null;
    const expectedQuantity = mergeTarget ? mergeTarget.quantity + quantity : quantity;

    /** @type {Record<string, number> | null} */
    let updates = config.updates ?? null;

    // Shopify only merges lines whose properties match, so a plan switch onto a plan another
    // line already uses would otherwise leave two lines (e.g. two Double subscription lines).
    if (mergeTarget) {
      updates = this.#groupUpdates(mergeTarget.keys, expectedQuantity);
      for (const key of sourceKeys) updates[key] = 0;
    }

    // A folded row covers several cart lines; after /cart/change.js moves the first one to the
    // new plan, the rest still sit on the old plan and must be removed.
    const needsGroupCleanup = isSellingPlanChange && !mergeTarget && sourceKeys.length > 1;

    /** @type {Record<string, unknown>} */
    const payload = {
      sections: sectionsToUpdate,
      sections_url: window.location.pathname,
    };

    if (updates) {
      payload.updates = updates;
    } else {
      payload.quantity = quantity;

      if (lineKey) {
        payload.id = lineKey;
      } else {
        payload.line = line;
      }

      if ('selling_plan' in config) {
        payload.selling_plan = sellingPlan === '' || sellingPlan === null ? '' : sellingPlan;
      }

      if (purchaseOption) {
        payload.properties = { _purchase_option: purchaseOption };
      }
    }

    const body = JSON.stringify(payload);
    const requestUrl = updates ? Theme.routes.cart_update_url : Theme.routes.cart_change_url;

    /** @param {Response} response */
    const readCart = async (response) => {
      const responseText = await response.text();

      if (!response.ok) {
        let errorPayload = { errors: `Cart update failed (${response.status})` };
        try {
          errorPayload = JSON.parse(responseText);
        } catch {
          /* non-JSON error body */
        }
        throw errorPayload;
      }

      return JSON.parse(responseText);
    };

    cartTotal?.shimmer();
    this.#setCheckoutDisabled(true);

    fetch(`${requestUrl}`, fetchConfig('json', { body }))
      .then(readCart)
      .then(async (cart) => {
        if (!needsGroupCleanup || cart.errors || !Array.isArray(cart.items)) return cart;

        // Shopify may move every split line to the new plan (not just the one changed), so
        // reconcile: nothing left on the old plan, exactly `quantity` on the new plan.
        const newPlanId = sellingPlan === '' || sellingPlan === null || sellingPlan === undefined ? '' : String(sellingPlan);
        const planOf = (/** @type {any} */ item) => String(item.selling_plan_allocation?.selling_plan?.id ?? '');
        const variantLines = cart.items.filter(
          (/** @type {any} */ item) => String(item.variant_id) === variantId && !bogoGiftCode(item)
        );
        const oldPlanLines = variantLines.filter((/** @type {any} */ item) => planOf(item) === sourcePlanId);
        const newPlanLines = variantLines.filter((/** @type {any} */ item) => planOf(item) === newPlanId);
        const newPlanTotal = newPlanLines.reduce((/** @type {number} */ sum, /** @type {any} */ item) => sum + item.quantity, 0);

        /** @type {Record<string, number>} */
        const cleanupUpdates = {};
        for (const item of oldPlanLines) cleanupUpdates[item.key] = 0;
        if (newPlanLines.length && newPlanTotal !== quantity) {
          Object.assign(
            cleanupUpdates,
            this.#groupUpdates(
              newPlanLines.map((/** @type {any} */ item) => item.key),
              quantity
            )
          );
        }
        if (!Object.keys(cleanupUpdates).length) return cart;

        const cleanupBody = JSON.stringify({
          updates: cleanupUpdates,
          sections: payload.sections,
          sections_url: payload.sections_url,
        });
        return fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body: cleanupBody })).then(readCart);
      })
      .then((parsedResponseText) => {
        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          if (isSellingPlanChange) {
            sectionRenderer.renderSection(this.sectionId, { cache: false });
          }
          return;
        }

        if (
          isSellingPlanChange &&
          !this.#verifySellingPlanChange(parsedResponseText, variantId, line, sellingPlan, expectedQuantity)
        ) {
          this.#handleCartError(line, { errors: 'Subscription option could not be updated. Please try again.' });
          sectionRenderer.renderSection(this.sectionId, { cache: false });
          return;
        }

        const sectionHtml = parsedResponseText.sections?.[this.sectionId];
        if (!sectionHtml) {
          sectionRenderer.renderSection(this.sectionId, { cache: false });
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        const morphMode = this.isDrawer && !isSellingPlanChange ? 'hydration' : 'full';
        morphSection(this.sectionId, sectionHtml, { mode: morphMode });

        this.#updateCartQuantitySelectorButtonStates();
        this.#syncBogoGifts(parsedResponseText);
      })
      .catch((error) => {
        console.error(error);
        if (isSellingPlanChange) {
          sectionRenderer.renderSection(this.sectionId, { cache: false });
        }
        this.#handleCartError(line, {
          errors:
            typeof error === 'object' && error !== null && 'errors' in error
              ? /** @type {{ errors: string }} */ (error).errors
              : 'Cart update failed. Please try again.',
        });
      })
      .finally(() => {
        if (isSellingPlanChange) {
          this.#sellingPlanUpdateInFlight = false;
        }
        this.#setCheckoutDisabled(false);
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Finds another top-level line with the same variant already on the target selling plan.
   * @param {number} line - 1-based source cart line.
   * @param {number | '' | null | undefined} sellingPlan
   * @returns {{ keys: string[], quantity: number } | null}
   */
  #findMergeTarget(line, sellingPlan) {
    const rows = this.refs.cartItemRows ?? [];
    const source = rows[line - 1];
    if (!source || source.dataset.parentKey || !source.dataset.variantId) return null;

    const planId = sellingPlan === '' || sellingPlan === null || sellingPlan === undefined ? '' : String(sellingPlan);
    const target = rows.find(
      (row) =>
        row !== source &&
        !row.dataset.parentKey &&
        !row.hasAttribute('data-group-follower') &&
        !row.hasAttribute('data-bogo-gift') &&
        row.dataset.key &&
        row.dataset.variantId === source.dataset.variantId &&
        (row.dataset.sellingPlanId ?? '') === planId
    );
    if (!target?.dataset.key) return null;

    const targetQuantity = Number.parseInt(target.dataset.quantity ?? '', 10);
    if (!Number.isFinite(targetQuantity) || targetQuantity < 1) return null;

    const keys = target.dataset.groupKeys ? target.dataset.groupKeys.split(',').filter(Boolean) : [target.dataset.key];
    return { keys, quantity: targetQuantity };
  }

  /**
   * Cart line keys folded into the row for a 1-based line (the row's own key first).
   * @param {number} line
   * @returns {string[]}
   */
  #getGroupKeys(line) {
    const row = this.refs.cartItemRows?.[line - 1];
    if (!row?.dataset.key) return [];
    const keys = row.dataset.groupKeys ? row.dataset.groupKeys.split(',').filter(Boolean) : [];
    return keys.length ? keys : [row.dataset.key];
  }

  /**
   * /cart/update.js payload putting the whole quantity on the first key and zeroing the rest.
   * @param {string[]} keys
   * @param {number} quantity
   * @returns {Record<string, number>}
   */
  #groupUpdates(keys, quantity) {
    /** @type {Record<string, number>} */
    const updates = {};
    keys.forEach((key, index) => {
      updates[key] = index === 0 ? quantity : 0;
    });
    return updates;
  }

  /**
   * Confirms the cart holds the expected quantity of the variant on the requested plan.
   * Summed across lines because Buy X Get Y discounts can split one line into several.
   * @param {Object} cart
   * @param {string} variantId
   * @param {number} line
   * @param {number | '' | null | undefined} expectedPlan
   * @param {number} expectedQuantity
   * @returns {boolean}
   */
  #verifySellingPlanChange(cart, variantId, line, expectedPlan, expectedQuantity) {
    if (!cart.items?.length) return false;

    const targetVariantId = variantId || String(cart.items[line - 1]?.variant_id ?? '');
    if (!targetVariantId) return false;

    const expectedPlanId = expectedPlan === '' || expectedPlan === null || expectedPlan === undefined ? null : expectedPlan;
    const total = cart.items
      .filter(
        (/** @type {any} */ item) =>
          String(item.variant_id) === targetVariantId &&
          !bogoGiftCode(item) &&
          (item.selling_plan_allocation?.selling_plan?.id ?? null) === expectedPlanId
      )
      .reduce((/** @type {number} */ sum, /** @type {any} */ item) => sum + item.quantity, 0);

    return total === expectedQuantity;
  }

  /**
   * @param {boolean} disabled
   */
  #setCheckoutDisabled(disabled) {
    for (const control of this.querySelectorAll('[data-cart-checkout], [name="checkout"][form="cart-form"]')) {
      if (!(control instanceof HTMLElement)) continue;
      control.toggleAttribute('aria-disabled', disabled);
      if (disabled) {
        control.setAttribute('tabindex', '-1');
      } else {
        control.removeAttribute('tabindex');
      }
    }
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    const cart = /** @type {any} */ (event.detail)?.resource;
    const attributeCode = isCartJson(cart) ? String(cart.attributes?.[BOGO_ACTIVE_ATTR] || '') : '';
    const codes = isCartJson(cart) && Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
    // Shopper removed the BOGO code themselves: treat it like removing the FREE row.
    if (attributeCode && !codes.some((/** @type {any} */ entry) => sameCode(entry.code, attributeCode))) {
      this.#setBogoOffer(false, attributeCode);
      return;
    }
    this.#handleCartUpdate(event);
  };

  /** @param {Event} event */
  #onBogoReactivateClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-bogo-reactivate]') : null;
    if (!(button instanceof HTMLElement) || !this.contains(button)) return;
    event.preventDefault();
    this.#setBogoOffer(true, button.dataset.bogoReactivate || '');
  };

  /**
   * Turns the BOGO offer off (FREE row bin / code removed: gifts removed, "Free tubs removed" note with an
   * "Add them back" button) or back on (that button). The code itself stays on the cart when switching off.
   * @param {boolean} on
   * @param {string} code
   */
  async #setBogoOffer(on, code) {
    if (!code || this.dataset.bogoMaxGifts === undefined) return;
    if (bogoSync.inFlight) {
      setTimeout(() => this.#setBogoOffer(on, code), 250);
      return;
    }
    bogoSync.inFlight = true;
    let updated = null;
    try {
      const cart = await this.#fetchCart();
      const state = bogoState(cart, 0);
      /** @type {Record<string, unknown>} */
      const payload = on
        ? { attributes: { [BOGO_ACTIVE_ATTR]: code, [BOGO_NOTICE_ATTR]: '' } }
        : {
            updates: bogoKeyUpdates(state.giftKeys, 0),
            attributes: { [BOGO_ACTIVE_ATTR]: '', [BOGO_NOTICE_ATTR]: `removed:${code}` },
          };
      if (!on) writeBogoHint(null);
      updated = await this.#postBogoUpdate(payload);
      if (on) writeBogoHint({ code, variant: state.variantId, token: updated.token });
      this.#renderBogoCart(updated);
    } catch (error) {
      console.warn('[cart] BOGO offer toggle failed', error);
    } finally {
      bogoSync.inFlight = false;
    }
    if (on && updated) this.#syncBogoGifts(updated);
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    const errorMessage =
      typeof parsedResponseText.errors === 'string'
        ? parsedResponseText.errors
        : Object.values(parsedResponseText.errors ?? {}).join(' ');

    cartItemError.textContent = errorMessage;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      const watching = this.#bogoWatching();
      (watching ? this.#syncBogoGifts() : Promise.resolve(false)).then((changed) => {
        if (!changed) sectionRenderer.renderSection(this.sectionId, { cache: false });
      });
      return;
    }
    if (event.target === this) return;

    const wasWatching = this.#bogoWatching();
    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }

    if (/** @type {any} */ (event.detail).data?.didError) return;
    if (wasWatching || this.#bogoWatching()) {
      // Product forms send the /cart/add.js response (added items only), so only reuse full cart JSON.
      const resource = /** @type {any} */ (event.detail).resource;
      this.#syncBogoGifts(isCartJson(resource) ? resource : undefined);
    }
  };

  /** @returns {Promise<any>} */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  }

  /**
   * POSTs /cart/update.js with this page's cart sections; throws on Shopify errors.
   * @param {Record<string, unknown>} payload
   * @returns {Promise<any>}
   */
  async #postBogoUpdate(payload) {
    const body = JSON.stringify({ ...payload, sections: this.#sectionsToUpdate(), sections_url: window.location.pathname });
    const response = await fetch(`${Theme.routes.cart_update_url}`, fetchConfig('json', { body }));
    const updated = await response.json();
    if (!response.ok || updated.errors || updated.status) throw updated;
    return updated;
  }

  /**
   * Morphs the cart sections from a BOGO update and tells the rest of the theme (cart icon, forms, pop-up pill).
   * @param {any} updated - /cart/update.js response with sections.
   */
  #renderBogoCart(updated) {
    const sectionHtml = updated.sections?.[this.sectionId];
    if (sectionHtml) {
      morphSection(this.sectionId, sectionHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
    this.#updateQuantitySelectors(updated);
    this.#updateCartQuantitySelectorButtonStates();

    // cart:update auto-opens the drawer, so a background sync (page load) with the drawer closed stays quiet;
    // the morphed header section already carries the new cart count.
    if (this.isDrawer && !this.closest('dialog')?.open) return;

    const itemCount = Number.isFinite(updated.item_count) ? updated.item_count : 0;
    this.dispatchEvent(
      new CartUpdateEvent(updated, this.sectionId, {
        itemCount,
        source: 'cart-items-component',
        sections: updated.sections,
      })
    );
  }

  /**
   * Two-way BOGO gift sync (see bogoState). While the offer is active: one gift line whose quantity equals the
   * paid tubs (adds or removes), the code re-applied if missing, the cart attributes kept current. If Shopify
   * reports the code as not applicable once the gifts are in, the gifts are removed and the offer switched off
   * with a notice, so nobody is charged $99.95 for a "free" tub. Inactive carts lose stray gift lines.
   * Enabled by data-bogo-max-gifts (theme setting "NEXT50 BOGO promo").
   * @param {any} [cart] - Latest cart JSON; fetched when omitted.
   * @returns {Promise<boolean>} Whether the cart was changed.
   */
  async #syncBogoGifts(cart) {
    const maxAttr = this.dataset.bogoMaxGifts;
    if (maxAttr === undefined) return false;
    if (bogoSync.inFlight) {
      bogoSync.pending = true;
      return false;
    }
    bogoSync.inFlight = true;
    const maxGifts = Number.parseInt(maxAttr, 10) || 0;
    let changed = false;

    try {
      // Each pass fixes what it can; a later pass sees Shopify's verdict on the code. Bounded to avoid loops.
      for (let pass = 0; pass < 3; pass += 1) {
        if (!isCartJson(cart)) cart = await this.#fetchCart();
        const state = bogoState(cart, maxGifts);
        document.dispatchEvent(
          new CustomEvent('next50:bogo-state', {
            detail: { active: state.active, code: state.code, freeTubs: state.active ? state.target : 0 },
          })
        );

        /** @type {Record<string, unknown> | null} */
        let payload = null;
        if (!state.active) {
          if (state.giftKeys.length) payload = { updates: bogoKeyUpdates(state.giftKeys, 0) };
        } else if (state.target > 0 && state.giftTotal === state.target && state.codeRejected) {
          writeBogoHint(null);
          payload = {
            updates: bogoKeyUpdates(state.giftKeys, 0),
            discount: '',
            attributes: { [BOGO_ACTIVE_ATTR]: '', [BOGO_NOTICE_ATTR]: 'rejected' },
          };
        } else {
          const needsGifts = state.giftTotal !== state.target && (state.giftKeys.length > 0 || state.variantId > 0);
          const needsCode = state.target > 0 && !state.codeApplied;
          if (needsGifts || needsCode || !state.attributesCurrent) {
            this.#disableCartItems();
            payload = {
              attributes: {
                [BOGO_ACTIVE_ATTR]: state.code,
                [BOGO_VARIANT_ATTR]: state.variantId ? String(state.variantId) : '',
                [BOGO_NOTICE_ATTR]: '',
              },
            };
            if (needsCode) payload.discount = state.code;
            if (needsGifts && state.giftKeys.length) {
              payload.updates = bogoKeyUpdates(state.giftKeys, state.target);
            } else if (needsGifts) {
              const body = JSON.stringify({
                items: [{ id: state.variantId, quantity: state.target, properties: { [BOGO_GIFT_PROPERTY]: state.code } }],
              });
              const added = await fetch(`${Theme.routes.cart_add_url}`, fetchConfig('json', { body }));
              const addedJson = await added.json();
              if (!added.ok || addedJson.status) throw addedJson;
            }
          }
        }

        if (!payload) break;
        this.#disableCartItems();
        cart = await this.#postBogoUpdate(payload);
        if (state.active && cart.attributes?.[BOGO_ACTIVE_ATTR]) {
          writeBogoHint({ code: state.code, variant: state.variantId, token: cart.token });
        }
        this.#renderBogoCart(cart);
        changed = true;
      }
      return changed;
    } catch (error) {
      console.warn('[cart] BOGO gift sync failed', error);
      return changed;
    } finally {
      this.#enableCartItems();
      bogoSync.inFlight = false;
      if (bogoSync.pending) {
        bogoSync.pending = false;
        setTimeout(() => this.#syncBogoGifts(), 0);
      }
    }
  }

  /**
   * Section ids of every cart-items-component on the page (drawer + cart page), comma-separated.
   * @returns {string}
   */
  #sectionsToUpdate() {
    const ids = new Set([this.sectionId]);
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) ids.add(item.dataset.sectionId);
    });
    return Array.from(ids).join(',');
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
